import { environment } from '../../../environments/environment';

/**
 * Silencia la consola del navegador en produccion.
 *
 * Por que existe: la consola de un cliente no es un canal de diagnostico, es una ventana a
 * las tripas de la app. Un `console.log` en un `computed()` del checkout llegaba a escribir
 * el detalle de validacion del formulario en cada ciclo de deteccion de cambios, y otros
 * volcaban configuracion interna del restaurante. Ademas hay ruido de terceros (pixel de
 * Meta, avisos del navegador) que no aporta nada al cliente y ensucia el diagnostico real.
 *
 * Se silencian los niveles informativos y se CONSERVA `console.error`: los 300+ que existen
 * viven en bloques `catch` y son la unica pista cuando hay que atender un incidente. Los
 * errores no capturados del navegador tampoco se tocan — fueron justamente los que
 * permitieron encontrar el worker roto de Mapbox (ver
 * docs/notes/2026-08-07-checkout-poligono-mapbox-worker.md).
 *
 * Esto NO reemplaza limpiar el codigo: un log que expone datos del cliente se borra en su
 * archivo. Este guard es la red que ataja lo que se cuele y lo que traen las librerias.
 */

/** Niveles que no deben llegar al navegador de un cliente. */
const SILENCED_METHODS = [
  'log',
  'debug',
  'info',
  'warn',
  'trace',
  'table',
  'dir',
  'dirxml',
  'group',
  'groupCollapsed',
  'groupEnd',
  'count',
  'time',
  'timeEnd',
  'timeLog',
] as const;

/**
 * @param isProduction sobreescribible SOLO para pruebas; en la app se usa el default, que
 *   lee el environment del build.
 */
export function configureProductionConsole(isProduction: boolean = environment.production): void {
  if (!isProduction) {
    return;
  }

  const noop = (): void => undefined;
  const target = console as unknown as Record<string, unknown>;

  SILENCED_METHODS.forEach((method) => {
    if (typeof target[method] === 'function') {
      target[method] = noop;
    }
  });

  installErrorSanitizer();
}

/**
 * `console.error` se conserva, pero NO tal cual: el patron del repo es
 * `console.error('mensaje', err)` con el `HttpErrorResponse` entero, y eso imprime dos cosas
 * que no deben verse en el navegador de nadie:
 *
 *  - `err.url` COMPLETA. Cuando SignalR no puede usar WebSocket cae a long-polling y manda
 *    el JWT en la query (`?access_token=...`); el error de negociacion arrastra esa URL, asi
 *    que el token del cajero terminaba impreso en consola.
 *  - `err.error`, que es el BODY de la respuesta del backend sin filtrar. En el menu publico,
 *    seguimiento de pedido y landing eso corre en el navegador del comensal.
 *
 * Se saneen los argumentos con forma de error HTTP y se deja pasar todo lo demas intacto
 * (incluidos los Error de JS con su stack, que son los que sirven para diagnosticar).
 */
function installErrorSanitizer(): void {
  const original = console.error.bind(console);
  console.error = (...args: unknown[]): void => original(...args.map(sanitizeArg));
}

/** Forma minima de un HttpErrorResponse sin depender de @angular/common/http aca. */
function isHttpErrorLike(value: unknown): value is { status: number; url?: string | null } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { status?: unknown }).status === 'number' &&
    'url' in value
  );
}

function sanitizeArg(arg: unknown): unknown {
  if (!isHttpErrorLike(arg)) {
    return arg;
  }

  const { status, url } = arg;
  const statusText = (arg as { statusText?: string }).statusText;
  const message = (arg as { message?: string }).message;

  return {
    status,
    statusText,
    // Sin query string: ahi viaja el access_token en el fallback de SignalR.
    url: typeof url === 'string' ? url.split('?')[0] : url,
    message,
  };
}
